import React, { useEffect, useRef, useState, useMemo } from 'react';
import { filterRoomsByLessonType } from '../../../../constants/roomTypes';
import { Box, Switch, TextField, Modal, Typography, Tooltip, Dialog, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import moment from 'moment';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonSelect from '../../../../form-components/CommonSelect';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { useForm } from 'react-hook-form';
import {
  createLesson,
  getGroupLessonTemplates,
  getGroupLessonTemplateById,
  previewGroupReserve,
} from '../../../../api/lesson';
import { errorMessages, addMinutesToTime, validateDateRange } from '../../../../utils/methods';
import { withOverlapConfirm } from '../../../../utils/overlapConfirm';
import { findDuplicateScheduleRowIndex, findOverlappingScheduleRowIndices } from '../../../../utils/scheduleValidation';

import AutoPublishCloseModal, { OPEN_SCHEDULE_TYPE, toOpenSchedulePayload, formatOpenSchedule } from './AutoPublishCloseModal';
import TicketSelectModal from './TicketSelectModal';
import TemplateSelectModal from './TemplateSelectModal';
import ReserveMemberSelectModal from './ReserveMemberSelectModal';

import {
  ScheduleCreateContainer,
  ModalHeader,
  ModalHeaderTitle,
  ScheduleCreateContent,
  SectionTitle,
  SettingGrid,
  SettingColumn,
  FieldGroup,
  FieldLabel,
  InfoText,
  DayButtonGroup,
  DayButton,
  ScheduleSection,
  ScheduleRow,
  ScheduleRowLabel,
  ScheduleActionButton,
  SettingButton,
  StatusChip,
  ModalFooter,
} from './GroupLessonTemplateStyled';

const DAY_LABELS = [
  { day: 1, label: '월' },
  { day: 2, label: '화' },
  { day: 3, label: '수' },
  { day: 4, label: '목' },
  { day: 5, label: '금' },
  { day: 6, label: '토' },
  { day: 0, label: '일' },
];

const DAY_SORT_ORDER = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };

const tealToggleSx = {
  width: 36, height: 20, padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: 'none' },
  '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 },
};

const DAY_NUMBER_TO_NAME = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

const DAY_NUMBER_TO_LABEL = { 0: '일요일', 1: '월요일', 2: '화요일', 3: '수요일', 4: '목요일', 5: '금요일', 6: '토요일' };


// 시 옵션 (0~23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i).padStart(2, '0'),
  label: String(i).padStart(2, '0'),
}));

// 분 옵션 (5분 단위)
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i * 5).padStart(2, '0'),
  label: String(i * 5).padStart(2, '0'),
}));

// "HH:mm" → { hour, minute }
const splitTime = time => {
  const [h, m] = (time || '00:00').split(':');
  return { hour: h || '00', minute: m || '00' };
};

// { hour, minute } → "HH:mm"
const joinTime = (hour, minute) => `${hour}:${minute}`;

const removeSeconds = time => time?.split(':').slice(0, 2).join(':') || '';

const LabelWithTooltip = ({ label, tooltip, required }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>
      {label}{required && <span style={{ color: '#ef5350', marginLeft: '2px' }}>*</span>}
    </Typography>
    <Tooltip title={<Typography sx={{ fontSize: '13px', lineHeight: 1.5 }}>{tooltip}</Typography>}
      arrow placement="top"
      componentsProps={{ tooltip: { sx: { backgroundColor: '#333', padding: '8px 14px', maxWidth: '260px', borderRadius: '6px' } } }}>
      <HelpOutlineIcon sx={{ fontSize: 15, color: '#bbb', cursor: 'help', '&:hover': { color: '#4AB3BC' } }} />
    </Tooltip>
  </Box>
);

const ScheduleCreateModal = ({
  open,
  onClose,
  user,
  lessonData,
  onSuccess,
  tickets = [],
  trainers = [],
  rooms = [],
  defaultRoomId = null, // 그룹 스케줄 설정의 기본 강의실 — 있으면 센터설정 기본 강의실보다 먼저 채운다
}) => {
  const toast = useToast();

  // 템플릿 관련
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // 수업 설정
  const [className, setClassName] = useState('');
  const [classDuration, setClassDuration] = useState('50');
  const [ticketDeductionCount, setTicketDeductionCount] = useState('1');
  const [membersMaxCount, setMembersMaxCount] = useState('');
  const [membersWaitMaxCount, setMembersWaitMaxCount] = useState('');
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  // 생성되는 모든 회차에 함께 예약할 회원(선택). 등록 전 미리보기로 회원×회차 판정을 보여준다.
  const [reserveMembers, setReserveMembers] = useState([]);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [reservePreview, setReservePreview] = useState(null); // {sessions, members, failureCount}
  const [previewLoading, setPreviewLoading] = useState(false);
  const openRef = useRef(open);
  // 미리보기에서 상담 겹침을 이미 확인했으면 등록도 force 로 보내 두 번 묻지 않는다
  const overlapForcedRef = useRef(false);
  openRef.current = open;
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  // 등록 기간
  const { control, watch, setValue } = useForm({
    defaultValues: {
      periodStart: lessonData?.periodStart || null,
      periodEnd: null,
    },
  });
  const periodStart = watch('periodStart');
  const periodEnd = watch('periodEnd');

  // 반복 요일
  const [selectedDays, setSelectedDays] = useState([]);

  // 메모
  const [memo, setMemo] = useState('');

  // 자동 공개&폐강
  const [autoSettingOpen, setAutoSettingOpen] = useState(false);
  const [autoSettings, setAutoSettings] = useState({
    openScheduledYn: 'N',
    openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS,
    openDate: '',
    openBeforeDays: '',
    openHour: '',
    openMinute: '',
    closeScheduledYn: 'N',
    closeHour: '',
    cancellationCapacity: '',
    closeMinCount: '',
  });

  // 스케줄 설정 (행 목록)
  const [scheduleRows, setScheduleRows] = useState([]);

  // 종료시간 자동 설정 토글
  const [autoEndTime, setAutoEndTime] = useState(true);

  const [loader, setLoader] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSelectLoading, setTemplateSelectLoading] = useState(false);
  const [errorField, setErrorField] = useState(null); // 첫 번째 필수값 에러 필드명
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openYnConfirm, setOpenYnConfirm] = useState(null);
  const [periodWarning, setPeriodWarning] = useState(null); // { index, checked }

  // ============================================
  // 초기화
  // ============================================
  useEffect(() => {
    if (open) {
      const clickedDate = lessonData?.periodStart;
      const currentDayInfo = lessonData?.currentDayInfo;
      const dayOfWeek = currentDayInfo?.week;
      const startTime = removeSeconds(currentDayInfo?.timeStart) || '00:00';
      const endTime = removeSeconds(currentDayInfo?.timeEnd) || '00:50';

      setClassName('');
      setClassDuration('');
      setTicketDeductionCount('');
      setMembersMaxCount('');
      setMembersWaitMaxCount('');
      setSelectedTicketIds([]);
      setSelectedDays(dayOfWeek !== undefined ? [dayOfWeek] : []);
      setMemo('');
      setAutoEndTime(true);
      setAutoSettings({
        openScheduledYn: 'N', openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openDate: '', openBeforeDays: '',
        openHour: '', openMinute: '',
        closeScheduledYn: 'N', closeHour: '', cancellationCapacity: '', closeMinCount: '',
      });
      setUseTemplate(false);
      setSelectedTemplateId('');
      setTicketModalOpen(false);
      setTemplateModalOpen(false);
      setErrorField(null);

      setValue('periodStart', clickedDate || null);
      setValue('periodEnd', clickedDate ? moment(clickedDate).add(6, 'days').toDate() : null);

      // 초기 스케줄 행: 클릭한 요일/시간 기반
      setScheduleRows([{
        id: Date.now(),
        dayOfWeek: dayOfWeek,
        startTime,
        endTime,
        trainerId: '',
        roomId: '',
        openYn: 'Y',
      }]);

      // 동시 예약 선택은 모달이 계속 마운트돼 있어 열 때마다 비운다(이전 수업 회원이 새 수업에 딸려가는 사고 방지).
      setReserveMembers([]);
      setReservePreview(null);
      setMemberModalOpen(false);

      // 템플릿 목록 로드
      if (user?.branch?.branchId && user?.token) {
        setTemplateLoading(true);
        getGroupLessonTemplates(user.branch.branchId, user.token, 1, 9999)
          .then(data => setTemplateList(data?.content ?? []))
          .catch(() => {})
          .finally(() => setTemplateLoading(false));
      }
    }
  }, [open]);

  // periodStart 변경 시 periodEnd 자동 계산
  useEffect(() => {
    if (periodStart) {
      setValue('periodEnd', moment(periodStart).add(6, 'days').toDate());
    }
  }, [periodStart]);

  // classDuration 변경 시 + autoEndTime ON이면 모든 행의 종료시간 재계산
  useEffect(() => {
    if (autoEndTime && classDuration) {
      setScheduleRows(prev =>
        prev.map(row => {
          if (!row.startTime) return row;
          const [h, m] = row.startTime.split(':').map(Number);
          const endTotal = h * 60 + m + Number(classDuration);
          const eH = String(Math.floor(endTotal / 60) % 24).padStart(2, '0');
          const eM = String(endTotal % 60).padStart(2, '0');
          return { ...row, endTime: `${eH}:${eM}` };
        }),
      );
    }
  }, [classDuration, autoEndTime]);

  // ============================================
  // 템플릿 선택
  // ============================================
  const handleTemplateSelect = async templateId => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    setTemplateSelectLoading(true);
    try {
      const tpl = await getGroupLessonTemplateById(templateId, user.token);
      if (!tpl) return;
      const duration = tpl.classDuration || 50;
      setClassDuration(duration.toString());
      if (tpl.ticketDeductionCount) setTicketDeductionCount(tpl.ticketDeductionCount.toString());
      if (tpl.membersMaxCount) setMembersMaxCount(tpl.membersMaxCount.toString());
      if (tpl.membersWaitMaxCount !== undefined) setMembersWaitMaxCount(tpl.membersWaitMaxCount.toString());
      if (tpl.ticketIds?.length > 0) applySelectedTicketIds(tpl.ticketIds);
      if (tpl.templateName) setClassName(tpl.templateName);

      // 스케줄 행 종료시간을 템플릿 수업시간 기준으로 즉시 재계산
      if (autoEndTime) {
        setScheduleRows(prev =>
          prev.map(row => {
            if (!row.startTime) return row;
            const [h, m] = row.startTime.split(':').map(Number);
            const endTotal = h * 60 + m + duration;
            const eH = String(Math.floor(endTotal / 60) % 24).padStart(2, '0');
            const eM = String(endTotal % 60).padStart(2, '0');
            return { ...row, endTime: `${eH}:${eM}` };
          }),
        );
      }
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setTemplateSelectLoading(false);
    }
  };

  // ============================================
  // 요일 토글
  // ============================================
  const sortRowsByDay = rows =>
    [...rows].sort((a, b) => (DAY_SORT_ORDER[a.dayOfWeek] ?? 99) - (DAY_SORT_ORDER[b.dayOfWeek] ?? 99));

  const toggleDay = day => {
    const isSelected = selectedDays.includes(day);

    if (isSelected) {
      // 최소 1개 요일 유지
      if (selectedDays.length <= 1) return;

      setSelectedDays(prev => prev.filter(d => d !== day));
      setScheduleRows(prev => {
        const filtered = prev.filter(row => row.dayOfWeek !== day);
        return filtered.length > 0 ? filtered : prev;
      });
    } else {
      setSelectedDays(prev => [...prev, day]);
      const firstRow = scheduleRows[0];
      const startTime = firstRow?.startTime || '00:00';
      const duration = Number(classDuration || 50);
      const [h, m] = startTime.split(':').map(Number);
      const endTotal = h * 60 + m + duration;
      const endTime = `${String(Math.floor(endTotal / 60) % 24).padStart(2, '0')}:${String(endTotal % 60).padStart(2, '0')}`;

      setScheduleRows(prev => sortRowsByDay([...prev, {
        id: Date.now(),
        dayOfWeek: day,
        startTime,
        endTime,
        trainerId: firstRow?.trainerId || '',
        roomId: firstRow?.roomId || '',
        openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : 'Y',
      }]));
    }
  };

  // ============================================
  // 스케줄 행 관리
  // ============================================
  const addScheduleRow = (index) => {
    const currentRow = scheduleRows[index];
    const duration = Number(classDuration) || 50;
    const [h, m] = (currentRow?.startTime || '00:00').split(':').map(Number);
    const newStartTotal = h * 60 + m + 60;
    const newStartTime = `${String(Math.floor(newStartTotal / 60) % 24).padStart(2, '0')}:${String(newStartTotal % 60).padStart(2, '0')}`;
    const newEndTotal = newStartTotal + duration;
    const newEndTime = `${String(Math.floor(newEndTotal / 60) % 24).padStart(2, '0')}:${String(newEndTotal % 60).padStart(2, '0')}`;

    const newRow = {
      id: Date.now(),
      dayOfWeek: currentRow?.dayOfWeek,
      startTime: newStartTime,
      endTime: newEndTime,
      trainerId: currentRow?.trainerId || '',
      roomId: currentRow?.roomId || '',
      openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : (currentRow?.openYn || 'Y'),
    };

    setScheduleRows(prev => [
      ...prev.slice(0, index + 1),
      newRow,
      ...prev.slice(index + 1),
    ]);
  };

  const copyScheduleRow = index => {
    const row = scheduleRows[index];
    setScheduleRows(prev => [
      ...prev.slice(0, index + 1),
      { ...row, id: Date.now(), openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : row.openYn },
      ...prev.slice(index + 1),
    ]);
  };

  const removeScheduleRow = index => {
    if (scheduleRows.length <= 1) return;
    const removedRow = scheduleRows[index];
    const newRows = scheduleRows.filter((_, i) => i !== index);
    setScheduleRows(newRows);
    // 해당 요일의 마지막 행이 삭제되면 반복요일에서도 해제
    if (removedRow && !newRows.some(r => r.dayOfWeek === removedRow.dayOfWeek)) {
      setSelectedDays(prev => prev.filter(d => d !== removedRow.dayOfWeek));
    }
  };

  const updateScheduleRow = (index, field, value) => {
    setScheduleRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (autoEndTime && classDuration) {
        const duration = Number(classDuration);
        if (field === 'startTime') {
          const [sH, sM] = value.split(':').map(Number);
          const newEnd = sH * 60 + sM + duration;
          updated[index].endTime = `${String(Math.floor(newEnd / 60) % 24).padStart(2, '0')}:${String(newEnd % 60).padStart(2, '0')}`;
        } else if (field === 'endTime') {
          const [eH, eM] = value.split(':').map(Number);
          const newStart = eH * 60 + eM - duration;
          if (newStart >= 0) {
            updated[index].startTime = `${String(Math.floor(newStart / 60) % 24).padStart(2, '0')}:${String(newStart % 60).padStart(2, '0')}`;
          }
        }
      }
      return updated;
    });
  };

  // ============================================
  // 제출
  // ============================================
  const handleSubmit = async () => {
    // 필수값 검증 — 첫 번째 에러만 표시
    if (!className.trim()) { setErrorField('className'); toast.error('수업명을 입력해주세요'); return; }
    if (selectedTicketIds.length === 0) { setErrorField('tickets'); toast.error('수강권을 선택해주세요'); return; }
    if (!classDuration || Number(classDuration) <= 0) { setErrorField('classDuration'); toast.error('수업 시간을 입력해주세요'); return; }
    if (Number(classDuration) % 5 !== 0) { setErrorField('classDuration'); toast.error('수업 시간은 5분 단위로 입력해주세요'); return; }
    if (!ticketDeductionCount || Number(ticketDeductionCount) <= 0) { setErrorField('ticketDeductionCount'); toast.error('수강권 차감 횟수를 입력해주세요'); return; }
    if (!membersMaxCount || Number(membersMaxCount) <= 0) { setErrorField('membersMaxCount'); toast.error('수업 정원을 입력해주세요'); return; }
    // 정원을 줄인 뒤 회원 선택이 정원을 넘으면 등록 시점에 막는다(타이핑 중 자동 절단은 '10'→'1'→'12' 같은 중간값에 선택을 날려서 폐기).
    if (reserveMembers.length > Number(membersMaxCount)) {
      setErrorField('membersMaxCount');
      toast.error(`함께 예약할 회원(${reserveMembers.length}명)이 수업 정원(${membersMaxCount}명)을 넘습니다. 정원을 늘리거나 회원을 줄여주세요`);
      return;
    }
    if (!periodStart || !periodEnd) { setErrorField('period'); toast.error('등록 기간을 설정해주세요'); return; }
    if (!validateDateRange(periodStart, periodEnd)) { setErrorField('period'); toast.error('기간을 다시 확인해주세요'); return; }
    if (selectedDays.length === 0) { setErrorField('days'); toast.error('반복 요일을 선택해주세요'); return; }

    // 등록 기간 90일 초과 경고
    const daysDiff = moment(periodEnd).diff(moment(periodStart), 'days');
    if (daysDiff > 90 && !periodWarning) {
      setPeriodWarning(daysDiff);
      return;
    }
    setPeriodWarning(null);

    // 모든 행에 강사/강의실 필수
    const emptyTrainerIdx = scheduleRows.findIndex(row => !row.trainerId);
    if (emptyTrainerIdx >= 0) { setErrorField('trainer'); toast.error(`${DAY_NUMBER_TO_LABEL[scheduleRows[emptyTrainerIdx].dayOfWeek] || ''} 스케줄의 강사를 선택해주세요`); return; }

    const emptyRoomIdx = scheduleRows.findIndex(row => !row.roomId);
    if (emptyRoomIdx >= 0) { setErrorField('room'); toast.error(`${DAY_NUMBER_TO_LABEL[scheduleRows[emptyRoomIdx].dayOfWeek] || ''} 스케줄의 강의실을 선택해주세요`); return; }

    // 시작시간 ≥ 종료시간 검사
    const invalidTimeIdx = scheduleRows.findIndex(row => row.startTime >= row.endTime);
    if (invalidTimeIdx >= 0) {
      const row = scheduleRows[invalidTimeIdx];
      setErrorField(`time_${invalidTimeIdx}`);
      toast.error(`${DAY_NUMBER_TO_LABEL[row.dayOfWeek] || ''} ${row.startTime}~${row.endTime} 스케줄의 시작시간이 종료시간보다 같거나 늦습니다`);
      return;
    }

    // 같은 요일+시간+강의실+강사 완전 중복 검사
    const duplicateIdx = findDuplicateScheduleRowIndex(scheduleRows);
    if (duplicateIdx >= 0) {
      const row = scheduleRows[duplicateIdx];
      setErrorField(`time_${duplicateIdx}`);
      toast.error(`${DAY_NUMBER_TO_LABEL[row.dayOfWeek] || ''} ${row.startTime}~${row.endTime} 스케줄이 중복됩니다`);
      return;
    }

    // 같은 요일+같은 강사 내 시간 겹침 검사 (한 강사가 동시에 두 수업 불가, 강의실은 무관)
    const overlappingPair = findOverlappingScheduleRowIndices(scheduleRows);
    if (overlappingPair) {
      const [i, j] = overlappingPair;
      const a = scheduleRows[i];
      const b = scheduleRows[j];
      setErrorField(`time_${j}`);
      toast.error(`${DAY_NUMBER_TO_LABEL[a.dayOfWeek] || ''} ${a.startTime}~${a.endTime}과 ${b.startTime}~${b.endTime} 스케줄의 시간이 겹칩니다`);
      return;
    }

    setErrorField(null);

    // 스케줄 행에서 classTimeInfoList 생성 — 각 행이 이미 요일 정보를 포함
    const classTimeInfoList = scheduleRows.map(row => ({
      dayOfWeek: DAY_NUMBER_TO_NAME[row.dayOfWeek],
      startTime: row.startTime,
      endTime: row.endTime,
      openYn: row.openYn || 'Y',
      trainerId: row.trainerId || null,
      exerciseRoomId: row.roomId || null,
    }));

    if (classTimeInfoList.length === 0) {
      toast.error('스케줄을 설정해주세요');
      return;
    }

    // 함께 예약할 회원이 있으면 등록 전에 회원×회차 판정을 받아 확인 모달에 보여준다(저장 없음).
    setReservePreview(null);
    overlapForcedRef.current = false;
    if (reserveMembers.length > 0) {
      setPreviewLoading(true);
      try {
        const preview = await withOverlapConfirm(force => {
          overlapForcedRef.current = force;
          return previewGroupReserve(buildPayload(), user.token, force);
        });
        // 응답을 기다리는 동안 모달이 닫혔으면 확인 창을 띄우지 않는다.
        if (!openRef.current) return;
        setReservePreview(preview);
      } catch (error) {
        if (openRef.current && !error?.silent) toast.error(errorMessages(error));
        return;
      } finally {
        setPreviewLoading(false);
      }
    }

    setConfirmOpen(true);
    return; // stop here, actual submit happens in handleConfirmSubmit
  };

  // createLesson / previewGroupReserve 가 같은 본문을 쓴다.
  const buildPayload = () => {
    // 스케줄 행에서 classTimeInfoList 생성
    const classTimeInfoList = scheduleRows.map(row => ({
      dayOfWeek: DAY_NUMBER_TO_NAME[row.dayOfWeek],
      startTime: row.startTime,
      endTime: row.endTime,
      openYn: row.openYn || 'Y',
      trainerId: row.trainerId || null,
      exerciseRoomId: row.roomId || null,
    }));

    // 수강권 분류
    const trialTicketIds = tickets
      .filter(t => selectedTicketIds.includes(t.courseTicketId) && t.ticketType === 'TRIAL_TICKET')
      .map(t => t.courseTicketId);
    const regularTicketIds = tickets
      .filter(t => selectedTicketIds.includes(t.courseTicketId) && t.ticketType !== 'TRIAL_TICKET')
      .map(t => t.courseTicketId);

    // trainerId — 첫 번째 행 기준 (디자인: 스케줄 행별 강사)
    // 현재 API는 trainerId 단일값만 받으므로, 첫 번째 행 강사를 사용
    const trainerId = scheduleRows[0]?.trainerId || null;
    const exerciseRoomId = scheduleRows[0]?.roomId || null;

    return {
      className: className.trim(),
      registrationStartDate: moment(periodStart).format('YYYY-MM-DD'),
      registrationEndDate: moment(periodEnd).format('YYYY-MM-DD'),
      classTimeInfoList,
      membersMaxCount: Number(membersMaxCount) || null,
      membersWaitMaxCount: Number(membersWaitMaxCount) || 0,
      exerciseRoomId,
      branchId: user.branch?.branchId,
      courseClassType: 'GROUP_LESSON',
      templateId: useTemplate && selectedTemplateId ? selectedTemplateId : null,
      trainerId,
      tickets: regularTicketIds,
      ticketId: regularTicketIds,
      trialTickets: trialTicketIds,
      allTicketsSelectedYn: selectedTicketIds.length === tickets.length && tickets.length > 0 ? 'Y' : 'N',
      openYn: autoSettings.openScheduledYn === 'Y' ? 'N' : (scheduleRows[0]?.openYn || 'Y'),
      openScheduledYn: autoSettings.openScheduledYn,
      ...toOpenSchedulePayload(autoSettings),
      closeScheduledYn: autoSettings.closeScheduledYn,
      closeScheduledMinCount: autoSettings.closeMinCount ? Number(autoSettings.closeMinCount) : null,
      closeScheduledTime: autoSettings.closeHour || null,
      courseTicketDeductionCount: Number(ticketDeductionCount) || 1,
      memo,
      reserveMemberIds: reserveMembers.map(m => m.memberId),
    };
  };

  const handleConfirmSubmit = async () => {
    setLoader(true);
    try {
      const result = overlapForcedRef.current
        ? await createLesson(buildPayload(), user.token, true)
        : await withOverlapConfirm(force => createLesson(buildPayload(), user.token, force));

      if (reserveMembers.length > 0) {
        // 회원 단위 전부-아니면-전무 — 한 회차라도 불가면 그 회원은 통째로 제외된다.
        const members = result?.members || [];
        const reservedMembers = members.filter(m => (m.failures || []).length === 0 && (m.reservedCount || 0) > 0).length;
        const excludedMembers = members.filter(m => (m.failures || []).length > 0).length;
        if (excludedMembers > 0) toast.warning(`수업이 등록되었습니다. 회원 ${reservedMembers}명 예약, 예약 불가 ${excludedMembers}명은 제외되었습니다`);
        else toast.success(`수업이 등록되었습니다. 회원 ${reservedMembers}명 전 회차 예약`);
      } else {
        toast.success('수업이 등록되었습니다');
      }
      setConfirmOpen(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      // 겹침 확인창에서 취소했으면 안내 없이 확인 모달을 유지한다
      if (!error?.silent) toast.error(errorMessages(error));
    }
    setLoader(false);
  };

  const selectedTicketCount = selectedTicketIds.length;

  // 미리보기 응답을 기다리는 중에는 닫지 못한다(닫힌 뒤 확인 창만 홀로 뜨는 것 방지).
  const closeUnlessPreviewing = () => { if (!previewLoading) onClose(); };

  // 회원 후보는 수강권 상품 기준(일일권 제외 — 예약 자동선택 경로와 동일)이라 수강권·기간이 있어야 조회할 수 있다.
  const regularTicketOptionsForMembers = useMemo(
    () => tickets
      .filter(t => selectedTicketIds.includes(t.courseTicketId) && t.ticketType !== 'TRIAL_TICKET')
      .map(t => ({ id: t.courseTicketId, name: t.courseTicketName })),
    [tickets, selectedTicketIds],
  );
  const regularTicketIdsForMembers = useMemo(() => regularTicketOptionsForMembers.map(t => t.id), [regularTicketOptionsForMembers]);
  const canPickMembers = regularTicketIdsForMembers.length > 0 && !!periodStart && !!periodEnd && Number(membersMaxCount) > 0;

  // 수강권 변경(직접 선택·템플릿 적용) 공통. 후보 기준인 수강권(일일권 제외) 집합이 바뀔 때만 회원 선택을 비운다.
  const applySelectedTicketIds = ids => {
    const regularKey = list => tickets
      .filter(t => list.includes(t.courseTicketId) && t.ticketType !== 'TRIAL_TICKET')
      .map(t => t.courseTicketId).sort().join(',');
    if (reserveMembers.length > 0 && regularKey(ids) !== regularKey(selectedTicketIds)) {
      setReserveMembers([]);
      toast.info('수강권이 바뀌어 함께 예약할 회원 선택을 초기화했습니다');
    }
    setSelectedTicketIds(ids);
  };

  // 강사 옵션
  const trainerOptions = trainers.map(t => ({
    value: t.trainerId,
    label: t.trainerName,
  }));

  // 강의실 옵션 — 그룹레슨실과 전체사용가능 룸만 고를 수 있다.
  const roomOptions = filterRoomsByLessonType(rooms, 'GROUP_LESSON').map(r => ({
    value: r.branchExerciseRoomId,
    label: r.branchExerciseRoomName,
  }));

  // 빈 행에 자동으로 채울 강의실 — 그룹 스케줄 설정 기본 강의실 → 센터설정 기본 강의실 → 고를 수 있는 곳이 하나뿐일 때 그곳.
  // 설정 강의실이 지금 고를 수 있는 목록에 없으면(삭제·유형 변경) 건너뛴다.
  const autoRoomId = useMemo(() => {
    const selectable = filterRoomsByLessonType(rooms, 'GROUP_LESSON');
    if (defaultRoomId != null && selectable.some(r => r.branchExerciseRoomId === defaultRoomId)) return defaultRoomId;
    const defaultRoom = selectable.find(r => r.defaultForGroupYn === 'Y');
    if (defaultRoom) return defaultRoom.branchExerciseRoomId;
    return selectable.length === 1 ? selectable[0].branchExerciseRoomId : null;
  }, [rooms, defaultRoomId]);

  useEffect(() => {
    if (autoRoomId == null) return;
    setScheduleRows(prev =>
      prev.some(row => !row.roomId) ? prev.map(row => (row.roomId ? row : { ...row, roomId: autoRoomId })) : prev,
    );
  }, [autoRoomId, scheduleRows]);

  return (
    <>
      <Modal open={open} onClose={closeUnlessPreviewing} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <ScheduleCreateContainer>
          <ModalHeader>
            <ModalHeaderTitle>그룹수업 스케줄 등록</ModalHeaderTitle>
            <CloseIcon onClick={closeUnlessPreviewing} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>

          <ScheduleCreateContent>
            <SectionTitle>수업 설정</SectionTitle>

            <SettingGrid>
              {/* 왼쪽 열: 템플릿, 수업명, 수강권 */}
              <SettingColumn>
                <FieldGroup>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LabelWithTooltip label="그룹수업 선택" tooltip="기존에 등록한 그룹수업을 불러옵니다" />
                    <Switch
                      checked={useTemplate}
                      onChange={e => {
                        setUseTemplate(e.target.checked);
                        if (!e.target.checked) setSelectedTemplateId('');
                      }}
                      sx={tealToggleSx}
                    />
                  </Box>
                  <Box sx={{ opacity: useTemplate ? 1 : 0.4, pointerEvents: useTemplate ? 'auto' : 'none' }}>
                    <Box
                      onClick={() => useTemplate && setTemplateModalOpen(true)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        height: '40px', px: '14px', border: '1px solid #c4c4c4', borderRadius: '6px',
                        cursor: useTemplate ? 'pointer' : 'default',
                        backgroundColor: selectedTemplateId ? '#f0f8f8' : '#fff',
                        transition: 'all 0.15s ease',
                        '&:hover': useTemplate ? { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' } : {},
                      }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: selectedTemplateId ? 500 : 400, color: selectedTemplateId ? '#4AB3BC' : '#999' }}>
                        {selectedTemplateId
                          ? templateList.find(t => t.id === selectedTemplateId)?.templateName || '선택됨'
                          : '그룹수업을 선택해 주세요.'}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                    </Box>
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="수업명" tooltip="캘린더에 표시될 수업 이름입니다" required />
                  <TextField size="small" fullWidth placeholder="수업명을 입력해 주세요." value={className}
                    onChange={e => { setClassName(e.target.value); if (errorField === 'className') setErrorField(null); }}
                    error={errorField === 'className'} />
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="수강권 선택" tooltip="이 수업에 사용할 수 있는 수강권을 선택합니다" required />
                  <Box onClick={() => setTicketModalOpen(true)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      height: '40px', px: '14px',
                      border: errorField === 'tickets' ? '2px solid #d32f2f' : '1px solid #c4c4c4',
                      borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: selectedTicketCount > 0 ? '#f0f8f8' : '#fff',
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: errorField === 'tickets' ? '#d32f2f' : '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' },
                    }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: selectedTicketCount > 0 ? 500 : 400, color: selectedTicketCount > 0 ? '#4AB3BC' : '#999' }}>
                      {selectedTicketCount > 0 ? `${selectedTicketCount}개 수강권 선택됨` : '수강권을 선택해 주세요'}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="함께 예약할 회원" tooltip="선택한 수강권을 보유한 회원(또는 회원 그룹)을 생성되는 모든 회차에 예약합니다. 수강권·등록 기간·정원을 먼저 정해야 하며, 등록 전 회차별 예약 가능 여부를 미리 보여줍니다" />
                  <Box
                    onClick={() => {
                      if (!canPickMembers) { toast.error('수강권·등록 기간·수업 정원을 먼저 설정해주세요'); return; }
                      setMemberModalOpen(true);
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      height: '40px', px: '14px', border: '1px solid #c4c4c4', borderRadius: '6px',
                      cursor: 'pointer', opacity: canPickMembers ? 1 : 0.5,
                      backgroundColor: reserveMembers.length > 0 ? '#f0f8f8' : '#fff',
                      transition: 'all 0.15s ease',
                      '&:hover': canPickMembers ? { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' } : {},
                    }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: reserveMembers.length > 0 ? 500 : 400, color: reserveMembers.length > 0 ? '#4AB3BC' : '#999' }}>
                      {reserveMembers.length > 0 ? `${reserveMembers.length}명 선택됨` : '회원을 선택해 주세요 (선택)'}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                  </Box>
                </FieldGroup>
              </SettingColumn>

              {/* 가운데 열: 수업시간, 차감횟수, 정원, 대기정원, 자동공개&폐강 */}
              <SettingColumn>
                <FieldGroup>
                  <Box sx={{ display: 'flex', gap: '12px' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <LabelWithTooltip label="수업 시간" tooltip="수업 1회의 진행 시간 (분)" required />
                      <TextField size="small" fullWidth placeholder="0" value={classDuration}
                        onChange={e => { setClassDuration(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'classDuration') setErrorField(null); }}
                        error={errorField === 'classDuration'}
                        InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>분</span> }} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <LabelWithTooltip label="수강권 차감 횟수" tooltip="예약 시 수강권에서 차감되는 횟수" required />
                      <TextField size="small" fullWidth placeholder="0" value={ticketDeductionCount}
                        onChange={e => { setTicketDeductionCount(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'ticketDeductionCount') setErrorField(null); }}
                        error={errorField === 'ticketDeductionCount'}
                        InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>회</span> }} />
                    </Box>
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <Box sx={{ display: 'flex', gap: '12px' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <LabelWithTooltip label="수업 정원" tooltip="수업에 예약할 수 있는 최대 인원" required />
                      <TextField size="small" fullWidth placeholder="0" value={membersMaxCount}
                        onChange={e => { setMembersMaxCount(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'membersMaxCount') setErrorField(null); }}
                        error={errorField === 'membersMaxCount'}
                        InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }} />
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <LabelWithTooltip label="대기 정원" tooltip="정원 초과 시 대기할 수 있는 최대 인원" />
                      <TextField size="small" fullWidth placeholder="0" value={membersWaitMaxCount}
                        onChange={e => setMembersWaitMaxCount(e.target.value.replace(/[^0-9]/g, ''))}
                        InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }} />
                    </Box>
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="자동 공개&폐강" tooltip="수업 공개/폐강을 자동으로 예약하는 설정" />
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <SettingButton onClick={() => setAutoSettingOpen(true)}>설정하기</SettingButton>
                    <StatusChip active={autoSettings.openScheduledYn === 'Y'}>공개</StatusChip>
                    <StatusChip active={autoSettings.closeScheduledYn === 'Y'}>폐강</StatusChip>
                  </Box>
                </FieldGroup>
              </SettingColumn>

              {/* 오른쪽 열: 등록기간, 반복요일, 메모 */}
              <SettingColumn>
                <FieldGroup>
                  <LabelWithTooltip label="등록 기간" tooltip="수업이 생성될 날짜 범위" required />
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    ...(errorField === 'period' && {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
                    }),
                  }}>
                    <FormInputDate name="periodStart" control={control} placeholder="시작일"
                      onChange={() => { if (errorField === 'period') setErrorField(null); }} />
                    <Typography>~</Typography>
                    <FormInputDate name="periodEnd" control={control}
                      minDate={periodStart ? moment(periodStart).add(6, 'days').toDate() : undefined} placeholder="종료일"
                      onChange={() => { if (errorField === 'period') setErrorField(null); }} />
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="반복 요일" tooltip="등록 기간 내 선택한 요일마다 수업이 반복 생성됩니다" required />
                  <DayButtonGroup sx={errorField === 'days' ? { border: '2px solid #d32f2f', borderRadius: '8px', padding: '4px' } : {}}>
                    {DAY_LABELS.map(({ day, label }) => (
                      <DayButton key={day} isActive={selectedDays.includes(day)}
                        onClick={() => { toggleDay(day); if (errorField === 'days') setErrorField(null); }}>
                        {label}
                      </DayButton>
                    ))}
                  </DayButtonGroup>
                </FieldGroup>
                <FieldGroup>
                  <LabelWithTooltip label="메모" tooltip="관리자용 메모 (회원에게 노출되지 않음)" />
                  <TextField size="small" fullWidth multiline rows={2} placeholder="메모를 입력해 주세요."
                    value={memo} onChange={e => setMemo(e.target.value)} />
                </FieldGroup>
              </SettingColumn>
            </SettingGrid>

            {/* ============================== */}
            {/* 스케줄 설정 섹션 */}
            {/* ============================== */}
            <ScheduleSection>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: '16px' }}>
                <SectionTitle sx={{ mb: 0 }}>스케줄 설정</SectionTitle>
              </Box>

              {(() => {
                const dayGroups = [];
                let currentDay = null;
                let currentGroup = [];

                scheduleRows.forEach((row, index) => {
                  if (row.dayOfWeek !== currentDay) {
                    if (currentGroup.length > 0) dayGroups.push({ day: currentDay, rows: currentGroup });
                    currentDay = row.dayOfWeek;
                    currentGroup = [{ row, index }];
                  } else {
                    currentGroup.push({ row, index });
                  }
                });
                if (currentGroup.length > 0) dayGroups.push({ day: currentDay, rows: currentGroup });

                return dayGroups.map(group => (
                  <Box key={group.day ?? 'none'} sx={{
                    border: '1px solid #e8e8e8', borderRadius: '8px', mb: '10px', overflow: 'hidden',
                  }}>
                    {group.rows.map(({ row, index }, groupIdx) => (
                      <ScheduleRow key={row.id} sx={{
                        borderBottom: groupIdx < group.rows.length - 1 ? '1px dashed #e8e8e8' : 'none',
                        margin: 0, borderRadius: 0, border: 'none',
                        ...(errorField === `time_${index}` && {
                          backgroundColor: '#FEF5F5',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D32F2F !important', borderWidth: '2px !important' },
                        }),
                      }}>
                        {groupIdx === 0 ? (
                          <ScheduleRowLabel>{row.dayOfWeek !== undefined ? DAY_NUMBER_TO_LABEL[row.dayOfWeek] || '' : ''}</ScheduleRowLabel>
                        ) : (
                          <ScheduleRowLabel sx={{ visibility: 'hidden' }}>{DAY_NUMBER_TO_LABEL[row.dayOfWeek] || ''}</ScheduleRowLabel>
                        )}
                        <CommonSelect value={splitTime(row.startTime).hour}
                          onChange={e => updateScheduleRow(index, 'startTime', joinTime(e.target.value, splitTime(row.startTime).minute))}
                          options={HOUR_OPTIONS} width={62} />
                        <Typography sx={{ fontSize: '13px', color: '#999' }}>:</Typography>
                        <CommonSelect value={splitTime(row.startTime).minute}
                          onChange={e => updateScheduleRow(index, 'startTime', joinTime(splitTime(row.startTime).hour, e.target.value))}
                          options={MINUTE_OPTIONS} width={62} />
                        <Typography sx={{ mx: '4px', color: '#bbb' }}>~</Typography>
                        <CommonSelect value={splitTime(row.endTime).hour}
                          onChange={e => updateScheduleRow(index, 'endTime', joinTime(e.target.value, splitTime(row.endTime).minute))}
                          options={HOUR_OPTIONS} width={62}  />
                        <Typography sx={{ fontSize: '13px', color: '#999' }}>:</Typography>
                        <CommonSelect value={splitTime(row.endTime).minute}
                          onChange={e => updateScheduleRow(index, 'endTime', joinTime(splitTime(row.endTime).hour, e.target.value))}
                          options={MINUTE_OPTIONS} width={62}  />
                        <Box sx={errorField === 'trainer' && !row.trainerId ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' } } : {}}>
                          <CommonSelect value={row.trainerId}
                            onChange={e => { updateScheduleRow(index, 'trainerId', e.target.value); if (errorField === 'trainer') setErrorField(null); }}
                            options={trainerOptions} emptyLabel="강사 선택" width={140} />
                        </Box>
                        <Box sx={errorField === 'room' && !row.roomId ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' } } : {}}>
                          <CommonSelect value={row.roomId}
                            onChange={e => { updateScheduleRow(index, 'roomId', e.target.value); if (errorField === 'room') setErrorField(null); }}
                            options={roomOptions} emptyLabel="강의실 선택" width={160} />
                        </Box>
                        <Switch checked={row.openYn === 'Y'}
                          onChange={e => {
                            if (autoSettings.openScheduledYn === 'Y') {
                              setOpenYnConfirm({ index, checked: e.target.checked });
                            } else {
                              updateScheduleRow(index, 'openYn', e.target.checked ? 'Y' : 'N');
                            }
                          }}
                          sx={{ ...tealToggleSx, ml: 'auto' }} />
                        <Typography sx={{ fontSize: '11px', color: row.openYn === 'Y' ? '#4AB3BC' : '#999', minWidth: '40px' }}>
                          {row.openYn === 'Y' ? '수업공개' : '수업비공개'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                          <ScheduleActionButton buttonVariant="add" onClick={() => addScheduleRow(index)}>
                            복제
                          </ScheduleActionButton>
                          <ScheduleActionButton buttonVariant="remove" onClick={() => removeScheduleRow(index)}
                            disabled={scheduleRows.length <= 1}>
                            삭제
                          </ScheduleActionButton>
                        </Box>
                      </ScheduleRow>
                    ))}
                  </Box>
                ));
              })()}
            </ScheduleSection>
          </ScheduleCreateContent>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={closeUnlessPreviewing} disabled={previewLoading}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSubmit} disabled={loader || previewLoading}>
              {loader ? '등록 중...' : previewLoading ? '예약 확인 중...' : '일정등록'}
            </CommonModalButton>
          </ModalFooter>
        </ScheduleCreateContainer>
      </Modal>

      {/* 자동 공개&폐강 모달 */}
      <AutoPublishCloseModal
        open={autoSettingOpen}
        onClose={() => setAutoSettingOpen(false)}
        settings={autoSettings}
        maxDate={periodStart}
        onSave={newSettings => {
          setAutoSettings(newSettings);
          if (newSettings.openScheduledYn === 'Y') {
            setScheduleRows(prev => prev.map(r => ({ ...r, openYn: 'N' })));
          } else {
            setScheduleRows(prev => prev.map(r => ({ ...r, openYn: 'Y' })));
          }
        }}
      />

      {/* 템플릿 선택 모달 */}
      <TemplateSelectModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        templateList={templateList}
        selectedTemplateId={selectedTemplateId}
        onSelect={handleTemplateSelect}
      />

      {/* 수강권 선택 모달 */}
      <TicketSelectModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        tickets={tickets}
        selectedIds={selectedTicketIds}
        onChangeIds={ids => { applySelectedTicketIds(ids); if (errorField === 'tickets') setErrorField(null); }}
      />

      {/* 함께 예약할 회원 선택 모달 */}
      <ReserveMemberSelectModal
        open={memberModalOpen}
        onClose={() => setMemberModalOpen(false)}
        token={user.token}
        branchId={user.branch?.branchId}
        ticketIds={regularTicketIdsForMembers}
        ticketOptions={regularTicketOptionsForMembers}
        startDate={periodStart ? moment(periodStart).format('YYYY-MM-DD') : ''}
        endDate={periodEnd ? moment(periodEnd).format('YYYY-MM-DD') : ''}
        maxCount={membersMaxCount}
        selectedMembers={reserveMembers}
        onChangeMembers={setReserveMembers}
      />

      {/* 등록 기간 경고 */}
      <Dialog open={!!periodWarning} onClose={() => setPeriodWarning(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: '24px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
            등록 기간이 {periodWarning}일입니다.
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '8px' }}>
            계속 진행하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={() => setPeriodWarning(null)}>취소</CommonModalButton>
          <CommonModalButton variant="submit" onClick={() => { setPeriodWarning(null); handleSubmit(); }}>진행</CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 자동공개 수동 변경 확인 */}
      <Dialog open={!!openYnConfirm} onClose={() => setOpenYnConfirm(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: '24px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
            자동공개가 설정되어 있습니다
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#888', mt: '8px' }}>
            공개 상태를 수동으로 변경하려면 자동 공개 설정을 먼저 해제해주세요.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={() => setOpenYnConfirm(null)}>닫기</CommonModalButton>
          <CommonModalButton variant="submit" onClick={() => {
            setOpenYnConfirm(null);
            setAutoSettingOpen(true);
          }}>자동공개 설정으로 이동</CommonModalButton>
        </DialogActions>
      </Dialog>

      {/* 일정 등록 확인 모달 */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '560px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
        }}>
          <ModalHeader>
            <ModalHeaderTitle>일정 등록 확인</ModalHeaderTitle>
            <CloseIcon onClick={() => setConfirmOpen(false)} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>
          <Box sx={{ p: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
            {/* Summary table */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', mb: '20px' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th': { fontSize: '13px', fontWeight: 600, color: '#555', textAlign: 'left', padding: '10px 14px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0', width: '100px' },
              '& td': { fontSize: '13px', color: '#333', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' },
              '& tr:last-child th, & tr:last-child td': { borderBottom: 'none' },
            }}>
              <tbody>
                <tr><th>수업명</th><td>{className}</td></tr>
                <tr><th>등록 기간</th><td>{periodStart ? moment(periodStart).format('YYYY-MM-DD') : ''} ~ {periodEnd ? moment(periodEnd).format('YYYY-MM-DD') : ''}</td></tr>
                <tr><th>수강권</th><td>{selectedTicketIds.length}개 선택됨</td></tr>
                <tr><th>수업 시간</th><td>{classDuration || '-'}분</td></tr>
                <tr><th>차감 횟수</th><td>{ticketDeductionCount || '-'}회</td></tr>
                <tr><th>정원</th><td>{membersMaxCount || '-'}명 / 대기 {membersWaitMaxCount || '0'}명</td></tr>
                {reserveMembers.length > 0 && (
                  <tr><th>함께 예약</th><td>{reserveMembers.length}명 · 생성 회차 {reservePreview?.sessions?.length ?? '-'}회</td></tr>
                )}
                {autoSettings.openScheduledYn === 'Y' && (
                  <tr><th>자동 공개</th><td>{formatOpenSchedule(autoSettings)}</td></tr>
                )}
                {autoSettings.closeScheduledYn === 'Y' && (
                  <tr><th>자동 폐강</th><td>수업 시작 {autoSettings.closeHour || '-'}시간 전, {autoSettings.closeMinCount || '-'}명 이하시</td></tr>
                )}
              </tbody>
            </Box>
            </Box>

            {/* Schedule list */}
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '8px' }}>
              스케줄 ({scheduleRows.length}건)
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse',
              '& th': { fontSize: '12px', fontWeight: 600, color: '#555', padding: '8px 12px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', textAlign: 'center' },
              '& td': { fontSize: '13px', color: '#333', padding: '8px 12px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' },
              '& tr:last-child td': { borderBottom: 'none' },
            }}>
              <thead>
                <tr><th>요일</th><th>시간</th><th>강사</th><th>강의실</th><th>공개</th></tr>
              </thead>
              <tbody>
                {scheduleRows.map((row, i) => {
                  const trainerName = trainers.find(t => t.trainerId === row.trainerId)?.trainerName || '-';
                  const roomName = rooms.find(r => r.branchExerciseRoomId === row.roomId)?.branchExerciseRoomName || '-';
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{DAY_NUMBER_TO_LABEL[row.dayOfWeek]?.replace('요일', '') || ''}</td>
                      <td>{row.startTime} ~ {row.endTime}</td>
                      <td>{trainerName}</td>
                      <td>{roomName}</td>
                      <td style={{ color: row.openYn === 'Y' ? '#0F7F85' : '#999', fontWeight: 600 }}>
                        {row.openYn === 'Y' ? '수업공개' : '수업비공개'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
            </Box>

            {/* 동시 예약 미리보기 — 회원별 예약 가능 회차 수와 실패 회차·사유 */}
            {reservePreview && reservePreview.members?.length > 0 && (
              <Box sx={{ mt: '20px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '8px' }}>
                  함께 예약 ({reservePreview.members.length}명 · {reservePreview.sessions.length}회차)
                </Typography>
                {reservePreview.members.some(m => m.failures.length > 0) && (
                  <Typography sx={{ fontSize: '12px', color: '#C2403D', mb: '8px' }}>
                    예약 불가 회원 {reservePreview.members.filter(m => m.failures.length > 0).length}명이 있습니다.
                    모든 회차에 예약 가능한 회원만 예약되며, 불가 회원은 통째로 제외됩니다.
                  </Typography>
                )}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                  {reservePreview.members.map(m => (
                    <Box key={m.memberId} sx={{ px: '12px', py: '8px', borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {/* 이름 클릭 → 회원 상세 '상품 상세' 탭, 판정에 쓴 수강권 카드로 스크롤·강조(새 탭 — 등록 폼을 잃지 않게) */}
                        <Typography
                          title="회원 상세의 해당 수강권으로 이동"
                          onClick={() => window.open(`/member/${m.memberId}?tab=three${m.memberCourseTicketId ? `&ticketId=${m.memberCourseTicketId}` : ''}`, '_blank')}
                          sx={{ fontSize: '13px', fontWeight: 600, color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px', '&:hover': { color: '#0F7F85' } }}>
                          {m.name}
                          <OpenInNewIcon sx={{ fontSize: 13, color: '#9aa4ab' }} />
                        </Typography>
                        {/* 회원 단위 판정 — 전 회차 가능이면 예약가능, 하나라도 불가면 회원 제외 */}
                        {m.failures.length === 0 ? (
                          <Box component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: '#0F7F85', backgroundColor: '#E4F5F6', borderRadius: '4px', px: '6px', py: '1px' }}>
                            예약가능 {m.reservedCount}회
                          </Box>
                        ) : (
                          <Box component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: '#C2403D', backgroundColor: '#FDF0EF', borderRadius: '4px', px: '6px', py: '1px' }}>
                            불가 {m.failures.length}회 · 제외
                          </Box>
                        )}
                      </Box>
                      {/* 불가 회차는 한 줄에 하나씩 */}
                      {m.failures.length > 0 && (
                        <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {m.failures.map(f => (
                            <Typography key={`${f.classDate}-${f.startTime}`} sx={{ fontSize: '12px', color: '#888', lineHeight: 1.5 }}>
                              {f.classDate.slice(5)} {f.startTime} · {f.reason}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={() => setConfirmOpen(false)}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleConfirmSubmit} disabled={loader}>
              {loader ? '등록 중...' : reservePreview?.members?.some(m => m.failures.length > 0) ? '예약 불가 제외 후 등록' : '등록하기'}
            </CommonModalButton>
          </ModalFooter>
        </Box>
      </Modal>
    </>
  );
};

export default ScheduleCreateModal;
